/**
 * Экран игры: каркас страницы и панель управления партией. Монтирует его `index.main.tsx`.
 *
 * Поле рисует game.tsx через движок сцены, стенд отладки — app.debug.tsx, связь держит app.socket.ts.
 * Здесь только разметка и отправка действий: правил игры на клиенте нет.
 */

import { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { bridge, store, stored, useBridge } from './app.socket.js'
import { PANEL, SIDEBAR_AVAILABLE, Sidebar } from './sidebar.js'
import { ClientRows, ConnectionRows, Log, logFilters } from './sidebar.debug.js'
import { Action, Block, EngineSelect, ROW_GRID } from './sidebar.blocks.js'

import { Lobby } from './lobby.js'
import { AvatarSprite } from './blocks/pawn.markup.js'
import { positionOf } from '../srv/protocol.js'
import { scenery } from './app.scene.js'
import { warmUp } from './engines/index.js'

const Board = lazy(() => import('./game.js').then((m) => ({ default: m.Board })))

/*
 * Что переживает перезагрузку:
 *   localStorage   sidebar-toggle — доступен ли сайдбар
 *                  tolk.harness   — раскрыта ли панель
 *   sessionStorage tolk.session  — код комнаты и токен возврата (в app.socket.ts)
 * Настройка интерфейса живёт дольше вкладки, сессия партии — нет. Имя, аватарка и
 * цвет игрока хранит вход (`lobby.tsx`).
 */
const HARNESS_KEY = 'tolk.harness'

const loadHarnessOpen = (): boolean => stored(HARNESS_KEY) === 'open'

/** Через сколько после начала ожидания появляется крутилка. */
const SPINNER_DELAY_MS = 150

/**
 * Ожидание первых данных. Пока список комнат не пришёл, лобби не показывают
 * вовсе: ни пустую надпись, ни формы — иначе экран сначала говорит «комнат
 * нет», а через мгновение перерисовывается.
 */
function Spinner() {
  // Быстрый ответ не должен успеть показать крутилку: мелькнув на кадр, она
  // выглядела бы тем же мерцанием, от которого её завели.
  const [shown, setShown] = useState(false)
  useEffect(() => {
    const id = setTimeout(() => setShown(true), SPINNER_DELAY_MS)
    return () => clearTimeout(id)
  }, [])
  if (!shown) return null
  return (
    <div
      role="status"
      className="flex min-h-full animate-fade-in items-center justify-center motion-reduce:animate-none"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className="size-8 animate-spin text-indigo-400 motion-reduce:animate-none"
      >
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" className="opacity-20" />
        <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
      <span className="sr-only">Loading</span>
    </div>
  )
}

/** Читаемая позиция фишки: сырой `c05` или `w12-03` игроку ничего не говорит. */
function whereIs(node: string): string {
  const { cellIndex, depth } = positionOf(node)
  return depth === 0 ? `cell ${cellIndex}` : `swamp ${cellIndex}\u2193${depth}`
}

/** Все комнаты с их кодами и удалением; прав на удаление пока нет. */
function Rooms() {
  const s = useBridge()
  const rooms = s.rooms ?? []

  return (
    <Block title="rooms">
      <ul className="space-y-1">
        {rooms.map((room) => (
          <li key={room.code} className={`${ROW_GRID} py-1 text-xs`}>
            <span className="min-w-0 truncate text-text" title={room.roomName}>
              {room.roomName}
            </span>
            <span className="shrink-0 whitespace-nowrap text-right font-mono text-[11px] text-steel-l">
              ({room.code}, {room.players}/{room.maxPlayers},{' '}
              {room.finished ? 'finished' : room.started ? 'live' : 'open'}
              {room.viewers > 0 && `, ${room.viewers} watching`})
            </span>
            <span className="text-right">
              <Action onClick={() => bridge.deleteRoom(room.code)} tone="bad">
                delete
              </Action>
            </span>
          </li>
        ))}
        {!rooms.length && <li className="text-xs text-path">empty</li>}
      </ul>
    </Block>
  )
}

/** Игрок и его активная вкладка сведены в одну строку по playerId. */
function People() {
  const s = useBridge()
  const snap = s.snapshot
  const players = snap?.players ?? []
  const tabs = s.harness?.clients ?? []
  const tabByPlayer = new Map<string, (typeof tabs)[number]>()
  for (const tab of tabs) if (tab.playerId) tabByPlayer.set(tab.playerId, tab)
  const linkedTabs = new Set(
    players.flatMap((player) => {
      const tab = tabByPlayer.get(player.id)
      return tab ? [tab.id] : []
    }),
  )

  return (
    <Block title="users">
      <ul className="space-y-1">
        {players.map((p) => {
          const tab = tabByPlayer.get(p.id)
          return (
            <li key={`player:${p.id}`} className={`${ROW_GRID} py-1 font-mono text-[11px]`}>
              <span
                className={`min-w-0 truncate ${tab ? (tab.id === s.socketId ? 'text-champ' : 'text-steel-l') : p.id === s.playerId ? 'text-champ' : 'text-text'}`}
              >
                {tab?.name ?? p.name}
              </span>
              <span className="flex min-w-0 items-center justify-end text-right">
                <span>(</span>
                {tab && (
                  <span className={`min-w-0 truncate ${p.id === s.playerId ? 'text-champ' : 'text-text'}`}>
                    {p.name}
                  </span>
                )}
                <span className="shrink-0 text-steel-l">
                  {tab ? ', ' : ''}
                  {whereIs(p.node)}
                </span>
                {!p.connected && <span className="shrink-0 text-danger">, off</span>}
                <span>)</span>
              </span>
              <span className="text-right">
                {tab && (
                  <Action onClick={() => bridge.eject(tab.id)} tone="bad">
                    eject
                  </Action>
                )}
              </span>
            </li>
          )
        })}
        <ClientRows exclude={linkedTabs} />
        {!players.length && !tabs.length && <li className="text-xs text-path">empty</li>}
      </ul>
    </Block>
  )
}

/** Состояние соединения относится к серверу, независимо от выбранной комнаты. */
/** Реализация сцены: html или pixi с выбором отрисовщика. */
function Scene() {
  return (
    <Block title="scene">
      <label className={`${ROW_GRID} text-xs`}>
        <span className="text-text">engine</span>
        <span />
        <EngineSelect />
      </label>
    </Block>
  )
}

function Connection() {
  return (
    <Block title="connection">
      <ConnectionRows />
    </Block>
  )
}

export function App() {
  const s = useBridge()
  // Состояние сайдбара читается синхронно при первом рендере: если бы оно
  // приходило из useEffect, первый кадр рисовался бы с неверной шириной и
  // панель дёргалась бы на каждой загрузке.
  const [debug, setDebug] = useState(loadHarnessOpen)
  // Спиннер — только пока не было ни одной неудачи: лобби с плашкой, однажды показанное, подменять им нельзя,
  // иначе на каждой попытке «Retry» плашка исчезает и появляется заново.
  const sawFailure = useRef(false)
  if (s.retry || s.unreachable) sawFailure.current = true
  const [logRoomCode, setLogRoomCode] = useState<string | null>(null)
  // Переходы включаются только со второго кадра, иначе восстановленная панель
  // на старте выезжала бы из нуля — то же мерцание, только анимированное.
  const [animate, setAnimate] = useState(false)

  useEffect(() => {
    bridge.connect()
    warmUp(scenery)
    bridge.hello(`tab-${Math.random().toString(36).slice(2, 6)}`)
    const id = requestAnimationFrame(() => setAnimate(true))
    return () => cancelAnimationFrame(id)
  }, [])

  useEffect(() => store(HARNESS_KEY, debug ? 'open' : 'closed'), [debug])

  const sidebarOpen = SIDEBAR_AVAILABLE && debug
  // Фильтр журнала: по умолчанию все строки; фильтр без строк (после `clear`) снимается.
  const selectedLogRoom = logRoomCode && logFilters(s).includes(logRoomCode) ? logRoomCode : null

  return (
    <div className="flex h-full">
      {/* Листы аватарок: одна копия на страницу, маркеры лобби и игры ссылаются на неё через `use`. */}
      <AvatarSprite />
      <Sidebar
        available={SIDEBAR_AVAILABLE}
        open={debug}
        onToggle={() => setDebug((v) => !v)}
        animate={animate}
      >
        {/*
          Колонка — флекс сверху вниз. Журнал внизу забирает остаточную высоту,
          блоки над ним прокручиваются сами, если их стало слишком много. Пока
          контейнер был обычным блоком с overflow-y-auto, сумма блоков выносила
          в прокрутку весь сайдбар целиком, даже когда места на экране хватало.
        */}
        <div className="min-h-0 overflow-y-auto">
          <Connection />
          <Scene />
          <Rooms />
          <People />
        </div>
        <Log roomCode={selectedLogRoom} onSelectRoom={setLogRoomCode} />
        {/* Дверь в графический стенд: служебная, поэтому в панели, а не в лобби. */}
        <a
          href="/?debug"
          className="shrink-0 px-4 pt-1 pb-3 text-center font-mono text-[11px] tracking-wide text-steel-l hover:text-text"
        >
          Open Animation Playground
        </a>
      </Sidebar>

      <main className="min-w-0 flex-1 overflow-y-auto">
        {s.snapshot && !s.lobbyView ? (
          /*
           * Поле появляется только вместе с партией. Своей рамки у него нет:
           * сцена лежит слоями во всё окно, как в графическом стенде, и
           * сдвигается на половину панели, когда та открыта.
           */
          <Suspense fallback={null}>
            <Board shift={sidebarOpen ? PANEL / 2 : 0} />
          </Suspense>
        ) : (
          /*
           * Пока идёт возврат в сохранённую партию, окно пустое; пока не пришёл список
           * комнат — крутилка, а при отказе сети лобби всё же открывается. Лобби здесь
           * было бы неправдой: снапшот придёт через мгновение, и вкладка
           * моргала бы «Start a game» на каждой перезагрузке.
           */
          !s.returning &&
          (s.rooms === null && !sawFailure.current ? (
            <Spinner />
          ) : (
            <Lobby shift={sidebarOpen ? PANEL / 2 : 0} />
          ))
        )}
      </main>
    </div>
  )
}
